import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
} from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  type TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  BILLING_ENTITY_REGISTRATION_NUMBER_REGION_MAP,
  BillingEntityRegistrationNumberType,
  type IBillingEntity,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  filterUndefined,
  type INamedDocument,
  isSameRef,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'pr-billing-entity-input',
  templateUrl: './billing-entity-input.component.html',
  styleUrls: ['./billing-entity-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BillingEntityInputComponent),
      multi: true,
    },
  ],
  imports: [
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltip,
    NgCommonModule,
  ],
})
export class BillingEntityInputComponent implements ControlValueAccessor {
  onChange: (_: unknown) => void;
  onTouched: (_: unknown) => void;
  practices$: Observable<INamedDocument<IPractice>[]>;
  filteredOptions$: Observable<string[]>;
  entityTypes$: Observable<BillingEntityRegistrationNumberType[]>;

  @Input() form: TypedFormGroup<IBillingEntity>;

  constructor(private _organisation: OrganisationService) {
    this.entityTypes$ = this._organisation.region$.pipe(
      filterUndefined(),
      map((region) => BILLING_ENTITY_REGISTRATION_NUMBER_REGION_MAP[region])
    );
  }

  writeValue(value: IBillingEntity): void {
    this.form.setValue(value);
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  clear(): void {
    this.form.reset();
  }

  isSelectedPracticeRef(
    practiceRef: DocumentReference<IPractice>,
    selectedPracticeRef: DocumentReference<IPractice>
  ): boolean {
    return isSameRef(practiceRef, selectedPracticeRef);
  }
}
